import { fabric } from 'fabric'
import type { FrameSize } from '../frame'
import WbFrame, { frameSizes } from '../frame'
import WbArticleImage from '../articleImage'
import WbImage from '../image'
import WbModelDetails from '../modelDetails'
import WbTextBox from '../textBox'
import WbShape from '../shape'
import type { IWhiteboardTemplate, IWhiteboardTemplateFileMapping, IWhiteboardTemplateOption } from './IWhiteboardTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, whiteboardConstants } from '@/models/constants'
import type MyArticle from '@/models/myArticle'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import utils from '@/services/utils'
import accessoriesTechSheetImage from '@/assets/templateLayoutImages/accessoriesTechSheet.png'

class AccessoriesTechSheet implements IWhiteboardTemplate {
  id = whiteboardConstants.frameTemplatesId.accessoriesTechSheet
  name = 'Accessories Tech Sheet'
  layoutImage = accessoriesTechSheetImage
  imgSize: number = 500
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  frameWidth: number = frameSizes.wide.width
  frameHeight: number = frameSizes.wide.height
  logoWidth = 155
  logoHeight = 87
  logoScaling = 20
  uaNameLogoWidth = 400
  uaNameLogoHeight = 38
  uaNameLogoScaling = 40
  leftMarginForModel = 30
  startPositionX = 0
  startPositionY = 0
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Neue Plak'
  rightMargin = 20
  frameSpacing = 50
  imageDefaultScaleFactor: number = 90
  leftPadding: number = 50
  rightPadding: number = 30
  textObjectsMaxWidth = 200
  articleImageProps = {
    bottomPadding: 50,
  }

  modelNameProps = {
    fontSize: 54,
    fontWeight: 'bold',
    topPadding: 40,
    leftPadding: 80,
    bottomPadding: 30,
    textAlign: 'left',
    lock: false,
    fill: '#ffffff',
  }

  modelDetailsProps = {
    padding: 17,
    fontSize: 14,
    width: 325,
    fontWeight: 'bold',
    textAlign: 'left',
    lock: false,
    fill: '#ffffff',
  }

  headerImageNameLogoProps = {
    bottomPadding: 180,
  }

  textBoxProps = {
    width: 325,
    topPadding: 20,
    bottomPadding: 10,
    leftPadding: 80,
    fontSize: 14,
    fontWeight: 'normal',
    textAlign: 'left',
    fill: '#ffffff',
    fontStyle: 'normal',
  }

  needToKnowstextBoxProps = {
    width: 350,
    topPadding: 10,
    bottomPadding: 145,
    leftPadding: 80,
    fontSize: 34,
    fontWeight: 'bold',
    textAlign: 'left',
    fill: '#ffffff',
    fontStyle: 'normal',
  }

  ponitsTextBoxProps = {
    topPadding: 40,
    leftPadding: 350,
  }

  circleObjProps = {
    bulletCount: 5,
    circleSpacing: 26,
    size: 20,
    width: 10,
    height: 10,
    topPadding: 20,
    rightPadding: 100,
    fill: '#FF0101',
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    fontStyle: 'normal',
  }

  attributesProps = {
    width: 325,
  }

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>): IWhiteboardTemplateOption[] {
    const options: IWhiteboardTemplateOption[] = []
    const modelAttributesWithSKU: IMyAttribute[] = []
    const defaultDisplayAttributesList = ['Department', 'Class', 'ModelLifecycle']
    const defaultAllowedDisplayAttributesList = defaultDisplayAttributesList.filter(attribute => myAttributes[attribute])
    defaultAllowedDisplayAttributesList.push(catalog.CatalogCode.toString())
    // model attributes
    const modellevelAttributes = Object.values(myAttributes).filter(attr => attr.IsModelLevel)
    for (const attr of modellevelAttributes) {
      modelAttributesWithSKU.push(attr)
    }

    // add current season SKU count
    const SKUField = { ...appConstants.staticFieldTemplate }
    SKUField.DisplayName = `${catalog.Season} SKU`
    SKUField.SystemName = catalog.CatalogCode.toString()
    SKUField.IsModelLevel = true
    SKUField.IsSKU = true
    modelAttributesWithSKU.push(SKUField)

    // hide restricted attributes
    const ControlAttributeVisibilityToDisplayInOutput = catalog?.Config.ControlAttributeVisibilityToDisplayInOutput
    let allowedAttributes = modelAttributesWithSKU
    if (ControlAttributeVisibilityToDisplayInOutput && ControlAttributeVisibilityToDisplayInOutput.length) {
      allowedAttributes = modelAttributesWithSKU.filter((attr) => {
        return !ControlAttributeVisibilityToDisplayInOutput.includes(attr.SystemName)
      })
    }
    options.push({
      name: 'displayAttributes',
      label: 'generateFrameDialog.steps.options.modelAttributesToDisplay',
      type: 'attributeChooser',
      default: defaultAllowedDisplayAttributesList,
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 5,
      data: allowedAttributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    // options.push({
    //   name: 'imageType',
    //   label: 'generateFrameDialog.steps.options.imageType',
    //   type: 'list',
    //   default: 'firstImage',
    //   data: whiteboardConstants.defaultImageType,
    //   required: false,
    //   multiple: false,
    //   valueProp: 'key',
    //   displayProp: 'value',
    // })

    // const availableFrameSizes: { key: FrameSize, label: string }[] = [
    //   { key: 'wide', label: 'Wide' },
    //   { key: 'a4', label: 'A4' },
    //   { key: 'letter', label: 'Letter' },
    // ]

    // options.push({
    //   name: 'frameSize',
    //   label: 'generateFrameDialog.steps.options.frameSize',
    //   type: 'list',
    //   default: 'wide',
    //   required: true,
    //   multiple: false,
    //   data: availableFrameSizes,
    //   valueProp: 'key',
    //   displayProp: 'label',
    // })

    return options
  }

  getFileMapping(): IWhiteboardTemplateFileMapping[] {
    return [{
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    }]
  }

  async generate(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articlesData: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, startPosition: IPoint, excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, startPosition, retailPg, wholesalePg, outletPg, isExporting)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, startPosition: IPoint, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    const objects: IWbObject[] = []
    this.imgSize = (this.imageWidth * this.imageDefaultScaleFactor) / 100
    this.startPositionX = isExporting ? 0 : startPosition.x ? startPosition.x : 0
    this.startPositionY = isExporting ? 0 : startPosition.y ? startPosition.y : 0
    const frameSize = 'wide' // options.frameSize
    const articleAttributes = options.displayAttributes

    const modelsMap = new Set<string>()
    const uniqueModelArticles: MyArticle[] = []
    for (const article of articles) {
      if (!modelsMap.has(article.ModelNumber)) {
        modelsMap.add(article.ModelNumber)
        uniqueModelArticles.push(article)
      }
    }

    let frameLeft = this.startPositionX
    const frameTop = this.startPositionY
    let frameCount = 0
    this.changePropsAsPerFrameSize(frameSize)
    for (const firstArticle of uniqueModelArticles) {
      const frameObjects: IWbObject[] = []

      frameObjects.push(await this.createHeaderLogo(
        frameTop + this.leftPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      frameObjects.push(await this.createHeaderNameLogo(
        frameTop + this.frameHeight - this.headerImageNameLogoProps.bottomPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      const modelNameOpt = {
        left: frameLeft + this.modelNameProps.leftPadding,
        top: frameTop + this.modelNameProps.topPadding,
        showLabels: false,
        width: this.frameWidth - this.modelNameProps.leftPadding - this.circleObjProps.rightPadding - this.circleObjProps.width,
        attributes: ['ModelName'],
        attributePlaceHolder: '[Blank]',
        fontFamily: this.font,
        fontSize: this.modelNameProps.fontSize,
        fontWeight: this.modelNameProps.fontWeight,
        textAlign: this.modelNameProps.textAlign,
        lock: this.modelNameProps.lock,
        fill: this.modelNameProps.fill,
      }
      const modelNameObject = await WbModelDetails.loadModelDetails(firstArticle, modelNameOpt)
      const modelNameHeight = this.getTextRequiredHeightBasedOnWidth(firstArticle.ModelName, this.frameWidth - 20, this.font, this.modelNameProps.fontSize, this.modelNameProps.fontWeight)
      const modelNameTop = frameTop + this.modelNameProps.topPadding
      modelNameObject.top = modelNameTop
      frameObjects.push(isExporting ? modelNameObject.toObject() as WbModelDetails : modelNameObject)

      const modelDetailsTop = (modelNameObject && modelNameObject.top && modelNameObject.height ? modelNameObject.top + modelNameHeight : frameTop + this.frameHeight / 2) + this.modelDetailsProps.padding
      const modelDetailsOpt = {
        left: frameLeft + this.modelNameProps.leftPadding,
        top: modelDetailsTop,
        showLabels: false,
        width: this.modelDetailsProps.width,
        attributes: ['ModelNumber', '_RetailPrice'],
        attributesSeparator: ' // ',
        attributePlaceHolder: '[Blank]',
        fontFamily: this.font,
        fontSize: this.modelDetailsProps.fontSize,
        fontWeight: this.modelDetailsProps.fontWeight,
        textAlign: this.modelDetailsProps.textAlign,
        lock: this.modelDetailsProps.lock,
        fill: this.modelDetailsProps.fill,
      }
      const modelDetailsObject = await WbModelDetails.loadModelDetails(firstArticle, modelDetailsOpt)
      frameObjects.push(isExporting ? modelDetailsObject.toObject() as WbModelDetails : modelDetailsObject)

      const imgLeft = frameLeft + (this.frameWidth - this.imgSize) / 2
      const imgTop = modelDetailsTop
      frameObjects.push(await this.createImage(
        imgTop,
        imgLeft,
        firstArticle,
        this.imageDefaultScaleFactor,
        options.imageType,
        isExporting,
      ))

      if (articleAttributes.length) {
        const attributesTop = modelDetailsTop + this.textBoxProps.topPadding
        frameObjects.push(
          ...await this.createArticleAttributes(
            firstArticle,
            articleAttributes,
            attributesTop,
            frameLeft + this.textBoxProps.leftPadding,
            isExporting,
            myAttributes,
            catalog,
          ),
        )
      }

      // bottom text box
      const needToKnowsTextBoxTop = frameTop + this.frameHeight - this.needToKnowstextBoxProps.bottomPadding
      const needToKnowsTextBoxLeft = frameLeft + (this.frameWidth - this.needToKnowstextBoxProps.width) / 2
      const needToKnowsTextBox: any = this.createTextObject(
        'NEED TO KNOWS',
        needToKnowsTextBoxTop,
        needToKnowsTextBoxLeft,
        this.needToKnowstextBoxProps.width,
        0,
        this.needToKnowstextBoxProps.fontSize,
        this.needToKnowstextBoxProps.fontStyle,
        this.needToKnowstextBoxProps.fontWeight,
        this.needToKnowstextBoxProps.textAlign,
        this.needToKnowstextBoxProps.fill,
      )
      frameObjects.push(needToKnowsTextBox)

      const pointsTextBoxTop = needToKnowsTextBoxTop + this.ponitsTextBoxProps.topPadding
      const pointsTextBox: any = this.createTextObject(
        '1.\n2.\n3.\n4.\n5.',
        pointsTextBoxTop,
        frameLeft + this.ponitsTextBoxProps.leftPadding,
        this.frameWidth - this.ponitsTextBoxProps.leftPadding,
        0,
        this.textBoxProps.fontSize,
        this.textBoxProps.fontStyle,
        this.textBoxProps.fontWeight,
        this.textBoxProps.textAlign,
        this.textBoxProps.fill,
      )
      frameObjects.push(pointsTextBox)

      // right numbered round text boxes
      for (let i = 0; i < this.circleObjProps.bulletCount; i++) {
        const circleLeft = frameLeft + this.frameWidth - this.circleObjProps.rightPadding - this.circleObjProps.width
        const circleTop = modelNameTop + this.circleObjProps.topPadding + i * (this.circleObjProps.height + this.circleObjProps.circleSpacing)
        const textCenterTop = circleTop + this.circleObjProps.height / 2
        const additionalPadddingLeft = i === 0 ? 2 : 1
        const textCenterLeft = circleLeft + this.circleObjProps.width / 2 + additionalPadddingLeft
        const numberTextBox = this.createTextObject(
          (i + 1).toString(),
          textCenterTop,
          textCenterLeft,
          this.circleObjProps.width,
          0,
          this.circleObjProps.fontSize,
          this.circleObjProps.fontStyle,
          this.circleObjProps.fontWeight,
          this.circleObjProps.textAlign,
          this.textBoxProps.fill,
        )

        const circleObject = this.createCircleObject(
          circleLeft,
          circleTop,
          this.circleObjProps.width,
          this.circleObjProps.height,
          this.circleObjProps.fill,
        )
        if (isExporting) {
          frameObjects.push(circleObject)
          frameObjects.push(numberTextBox)
        }
        else {
          frameObjects.push(numberTextBox)
          frameObjects.push(circleObject)
        }
      }

      objects.push(...frameObjects)
      objects.push(new WbFrame(
        frameSize,
        {
          children: frameObjects.map(obj => obj.id || ''),
          name: firstArticle.ModelName,
          left: frameLeft,
          top: frameTop,
          sortOrder: frameCount,
          lock: false,
          fill: '#000000',
        },
      ))

      frameLeft += isExporting ? 0 : this.frameWidth + this.frameSpacing
      frameCount++
    }

    return objects
  }

  changePropsAsPerFrameSize(frameSize: FrameSize) {
    this.frameWidth = frameSizes[frameSize].width
    this.frameHeight = frameSizes[frameSize].height
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const minNumberOfLines = Math.ceil(utils.getTextWidth(text, `${fontWeight} ${fontSize}px ${font}`) / availableWidth)
    let multiLineSampleText = ''
    for (let index = 0; index < minNumberOfLines; index++) {
      multiLineSampleText += text
      if (index < minNumberOfLines - 1) {
        multiLineSampleText += '\n'
      }
    }
    return utils.getTextHeight(multiLineSampleText, fontSize, font, fabric)
  }

  async createHeaderNameLogo(top: number, left: number, isExporting: boolean = false) {
    const value = { scale: this.uaNameLogoScaling }
    const options = {
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaNameLogoWhite, value, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaNameLogoWhite, options).then((newImg) => {
        newImg.setProp('scale', value)
        return newImg
      })
    }
  }

  async createHeaderLogo(top: number, left: number, isExporting: boolean = false) {
    const value = { scale: this.logoScaling }
    const options = {
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaIconWhite, value, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaIconWhite, options).then((newImg) => {
        newImg.setProp('scale', value)
        return newImg
      })
    }
  }

  async createImage(top: number, left: number, article: MyArticle, imageScaleFactor: number, imageType: string, isExporting: boolean) {
    const scaleValue = { scale: imageScaleFactor }
    if (isExporting) {
      return await WbArticleImage.getJsonObject(article, 500, 500, scaleValue, { top, left, catalogCode: article.CatalogCode, articleId: article.Id, objectId: article.CatalogArticleId, isRequest: article._IsRequestArticle, assetKey: imageType })
    }
    else {
      return await WbArticleImage.loadArticleImage(article, 500, 500, { top, left, catalogCode: article.CatalogCode, articleId: article.Id, objectId: article.CatalogArticleId, isRequest: article._IsRequestArticle, assetKey: imageType }).then((newImg) => {
        newImg.setProp('scale', scaleValue)
        return newImg
      })
    }
  }

  createTextObject(text: string, top: number, left: number, width: number, height: number, fontSize: number, fontStyle, fontWeight: string, textAlign: string, color: string) {
    text = text != null ? text.toString() : ''
    return new WbTextBox(text, {
      top,
      left,
      width,
      height,
      fill: color,
      fontFamily: this.font,
      fontSize,
      fontStyle,
      fontWeight,
      textAlign,
      lock: false,
    })
  }

  createCircleObject(left: number, top: number, width: number, height: number, fill = 'transparent') {
    const scaleX = width / this.circleObjProps.size
    const scaleY = height / this.circleObjProps.size

    return new WbShape('circle', {
      top,
      left,
      fill,
      opacity: 1,
      scaleX,
      scaleY,
      stroke: '#888888',
      lock: false,
    })
  }

  async createArticleAttributes(article: MyArticle, articleAttributes: string[], top: number, left: number, isExporting: boolean = false, myAttributes: Record<string, IMyAttribute>, catalog: CatalogDetails) {
    const attributeObjects: IWbObject[] = []
    const modelAttributesWithSKU: Record<string, IMyAttribute> = {}
    const modellevelAttributes = Object.values(myAttributes).filter(attr => attr.IsModelLevel)
    for (const key in modellevelAttributes) {
      modelAttributesWithSKU[key] = myAttributes[key]
    }
    // add current season SKU count
    const SKUField = { ...appConstants.staticFieldTemplate }
    SKUField.DisplayName = `${catalog.Season} SKU`
    SKUField.SystemName = catalog.CatalogCode.toString()
    SKUField.IsModelLevel = true
    SKUField.IsSKU = true
    modelAttributesWithSKU[catalog.CatalogCode.toString()] = SKUField

    const headerOpt = {
      left,
      top,
      showLabels: false,
      width: this.attributesProps.width,
      attributes: articleAttributes,
      attributesSeparator: '\n',
      attributePlaceHolder: '[Blank]',
      fontFamily: this.font,
      fontSize: this.modelDetailsProps.fontSize,
      fontWeight: this.modelDetailsProps.fontWeight,
      textAlign: this.modelDetailsProps.textAlign,
      lock: this.modelDetailsProps.lock,
      fill: this.modelDetailsProps.fill,
    }
    const modelObject = await WbModelDetails.loadModelDetails(article, headerOpt)
    attributeObjects.push(isExporting ? modelObject.toObject() as WbModelDetails : modelObject)

    return attributeObjects
  }
}

export default new AccessoriesTechSheet()
